import React, { useState } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import { withTranslation } from 'react-i18next'
import CustomLoader from '../components/Loader/CustomLoader'
import DataTable from 'react-data-table-component'
import orderBy from 'lodash/orderBy'
import { getCuisines, editCuisine, deleteCuisine, createCuisine } from '../apollo'
import {
  Box, Container, Typography, Paper, TextField, InputAdornment,
  IconButton, Menu, MenuItem, ListItemIcon, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, CircularProgress, Switch, Chip
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import StorefrontIcon from '@mui/icons-material/Storefront'
import LocalGroceryStoreIcon from '@mui/icons-material/LocalGroceryStore'

const GET_CUISINES = gql`${getCuisines}`
const EDIT_CUISINE = gql`${editCuisine}`
const DELETE_CUISINE = gql`${deleteCuisine}`
const CREATE_CUISINE = gql`${createCuisine}`

function CuisineActionMenu({ row, onEdit, onDelete }) {
  const [anchor, setAnchor] = useState(null)
  return (
    <>
      <IconButton size="small" onClick={e => setAnchor(e.currentTarget)} sx={{ color: '#94a3b8', '&:hover': { bgcolor: '#f1f5f9' } }}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
        PaperProps={{ elevation: 0, sx: { borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: '1px solid #f1f5f9', minWidth: 160 } }}>
        <MenuItem onClick={() => { setAnchor(null); onEdit(row) }} sx={{ borderRadius: '8px', mx: 0.5, my: 0.25, fontSize: '0.83rem' }}>
          <ListItemIcon><EditIcon fontSize="small" sx={{ color: '#3b82f6' }} /></ListItemIcon>Edit
        </MenuItem>
        <MenuItem onClick={() => { setAnchor(null); onDelete(row._id) }} sx={{ borderRadius: '8px', mx: 0.5, my: 0.25, fontSize: '0.83rem', color: '#ef4444' }}>
          <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: '#ef4444' }} /></ListItemIcon>Delete
        </MenuItem>
      </Menu>
    </>
  )
}

function Cuisines() {
  const [searchQuery, setSearchQuery] = useState('')
  const [addModal, setAddModal] = useState(false)
  const [editModal, setEditModal] = useState(false)
  const [selected, setSelected] = useState(null)
  const [newName, setNewName] = useState('')
  const [newShopType, setNewShopType] = useState('restaurant')
  const [editName, setEditName] = useState('')
  const [editShopType, setEditShopType] = useState('restaurant')
  const [addError, setAddError] = useState('')
  const [addSuccess, setAddSuccess] = useState(false)

  const { data, loading, error } = useQuery(GET_CUISINES)
  const [doCreate, { loading: creating }] = useMutation(CREATE_CUISINE, {
    refetchQueries: [{ query: GET_CUISINES }],
    onCompleted: () => setAddSuccess(true),
    onError: err => setAddError(err.message)
  })
  const [doEdit, { loading: editing }] = useMutation(EDIT_CUISINE, {
    refetchQueries: [{ query: GET_CUISINES }],
    onCompleted: () => setEditModal(false)
  })
  const [doDelete] = useMutation(DELETE_CUISINE, {
    refetchQueries: [{ query: GET_CUISINES }],
    onError: () => {}
  })

  const handleDelete = (id) => doDelete({ variables: { id } })

  const allCuisines = data?.cuisines ?? []
  const restaurantCount = allCuisines.filter(c => c.shopType === 'restaurant' || !c.shopType).length
  const groceryCount = allCuisines.filter(c => c.shopType === 'grocery').length
  const regex = searchQuery.length > 2 ? new RegExp(searchQuery.toLowerCase(), 'g') : null
  const filtered = searchQuery.length < 3 ? allCuisines : allCuisines.filter(c => c.name?.toLowerCase().search(regex) > -1)

  const openAdd = () => { setNewName(''); setNewShopType('restaurant'); setAddError(''); setAddSuccess(false); setAddModal(true) }
  const openEdit = row => { setSelected(row); setEditName(row.name); setEditShopType(row.shopType || 'restaurant'); setEditModal(true) }
  const handleCreate = () => {
    if (!newName.trim()) { setAddError('Name is required'); return }
    doCreate({ variables: { cuisineInput: { name: newName.trim(), shopType: newShopType } } })
  }
  const handleEdit = () => doEdit({ variables: { cuisineInput: { _id: selected._id, name: editName, shopType: editShopType } } })

  const shopTypeBadge = type => type === 'grocery'
    ? <Chip icon={<LocalGroceryStoreIcon sx={{ fontSize: '12px !important' }} />} label="Grocery" size="small" sx={{ fontSize: '0.68rem', bgcolor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', height: 22 }} />
    : <Chip icon={<StorefrontIcon sx={{ fontSize: '12px !important' }} />} label="Restaurant" size="small" sx={{ fontSize: '0.68rem', bgcolor: '#eff6ff', color: '#3b82f6', border: '1px solid #bfdbfe', height: 22 }} />

  const columns = [
    {
      name: 'CUISINE', sortable: true, selector: row => row.name, minWidth: '200px',
      cell: row => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: '#fef3c7', border: '1px solid #fde68a', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: '#d97706' }}>{row.name?.charAt(0)?.toUpperCase()}</Typography>
          </Box>
          <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#1e293b' }}>{row.name}</Typography>
        </Box>
      )
    },
    {
      name: 'TYPE', hide: 'sm', minWidth: '140px',
      cell: row => shopTypeBadge(row.shopType)
    },
    { name: '', right: true, width: '60px', cell: row => <CuisineActionMenu row={row} onEdit={openEdit} onDelete={handleDelete} /> }
  ]

  const ShopTypeToggle = ({ value, onChange }) => (
    <Box sx={{ display: 'flex', gap: 1 }}>
      {['restaurant', 'grocery'].map(t => (
        <Box key={t} onClick={() => onChange(t)} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.75, borderRadius: '10px', cursor: 'pointer', border: `1px solid ${value === t ? '#6366f1' : '#e2e8f0'}`, bgcolor: value === t ? '#eef2ff' : '#fff', transition: 'all 0.15s' }}>
          {t === 'grocery' ? <LocalGroceryStoreIcon sx={{ fontSize: 15, color: value === t ? '#6366f1' : '#94a3b8' }} /> : <StorefrontIcon sx={{ fontSize: 15, color: value === t ? '#6366f1' : '#94a3b8' }} />}
          <Typography sx={{ fontSize: '0.8rem', fontWeight: value === t ? 700 : 400, color: value === t ? '#4f46e5' : '#64748b', textTransform: 'capitalize' }}>{t}</Typography>
        </Box>
      ))}
    </Box>
  )

  return (
    <>
      <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #78350f 60%, #92400e 100%)', px: { xs: 2, md: 4 }, pt: 3.5, pb: 5, position: 'relative', overflow: 'hidden', '&::before': { content: '""', position: 'absolute', top: -60, right: -60, width: 260, height: 260, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' } }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 52, height: 52, borderRadius: '14px', background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <RestaurantMenuIcon sx={{ color: '#fcd34d', fontSize: 26 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', color: '#fff', lineHeight: 1.15, letterSpacing: '-0.02em' }}>Cuisines</Typography>
              <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', mt: 0.3 }}>Manage cuisine categories for restaurants and grocery</Typography>
            </Box>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}
            sx={{ background: 'linear-gradient(135deg,#f59e0b,#d97706)', color: '#fff', fontWeight: 700, px: 3, py: 1.2, borderRadius: '12px', boxShadow: '0 4px 16px rgba(245,158,11,0.45)', textTransform: 'none', '&:hover': { background: 'linear-gradient(135deg,#d97706,#b45309)', transform: 'translateY(-1px)' }, transition: 'all 0.2s ease' }}>
            Add Cuisine
          </Button>
        </Box>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 3, pb: 4 }}>
        <Box display="flex" gap={2} mb={3} flexWrap="wrap">
          {[
            { label: 'Total Cuisines', value: allCuisines.length, color: '#f59e0b', bg: 'linear-gradient(135deg,#fffbeb,#fef3c7)', border: '#fde68a', icon: <RestaurantMenuIcon sx={{ fontSize: 22 }} /> },
            { label: 'Restaurant', value: restaurantCount, color: '#3b82f6', bg: 'linear-gradient(135deg,#eff6ff,#dbeafe)', border: '#bfdbfe', icon: <StorefrontIcon sx={{ fontSize: 22 }} /> },
            { label: 'Grocery', value: groceryCount, color: '#16a34a', bg: 'linear-gradient(135deg,#f0fdf4,#dcfce7)', border: '#bbf7d0', icon: <LocalGroceryStoreIcon sx={{ fontSize: 22 }} /> },
          ].map(stat => (
            <Paper key={stat.label} elevation={0} sx={{ background: stat.bg, border: `1px solid ${stat.border}`, borderRadius: '16px', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', flex: '1 1 140px', minWidth: 140 }}>
              <Box sx={{ width: 44, height: 44, borderRadius: '12px', background: `${stat.color}18`, border: `1px solid ${stat.color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: stat.color, flexShrink: 0 }}>{stat.icon}</Box>
              <Box>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.1 }}>{stat.value}</Typography>
                <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 500, mt: 0.2 }}>{stat.label}</Typography>
              </Box>
            </Paper>
          ))}
        </Box>

        <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
          <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', background: '#fafbfc' }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>
              All Cuisines <Typography component="span" sx={{ ml: 1, fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>({filtered.length})</Typography>
            </Typography>
            <TextField size="small" placeholder="Search cuisines..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>, sx: { borderRadius: '10px', fontSize: '0.83rem', bgcolor: '#fff' } }}
              sx={{ width: 260 }} />
          </Box>
          {error && <Box sx={{ px: 3, py: 2, bgcolor: '#fef2f2' }}><Typography sx={{ fontSize: '0.83rem', color: '#dc2626' }}>Error: {error.message}</Typography></Box>}
          {loading ? <CustomLoader /> : (
            <DataTable columns={columns} data={filtered} pagination noHeader
              sortFunction={(rows, field, dir) => orderBy(rows, row => row[field]?.toString().toLowerCase(), dir)}
              customStyles={{ headRow: { style: { backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', minHeight: '44px' } }, headCells: { style: { fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.06em', textTransform: 'uppercase' } }, rows: { style: { borderBottom: '1px solid #f1f5f9', minHeight: '60px', '&:hover': { backgroundColor: '#f8fafc' } } }, cells: { style: { paddingLeft: '16px', paddingRight: '16px' } } }}
              noDataComponent={<Box py={8} textAlign="center"><RestaurantMenuIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} /><Typography sx={{ color: '#64748b' }}>No cuisines found</Typography></Box>}
            />
          )}
        </Paper>
      </Container>

      {/* ADD MODAL */}
      <Dialog open={addModal} onClose={() => !creating && setAddModal(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2.5, px: 3, pb: 1 }}>
          <Typography fontWeight={700} fontSize="1rem">{addSuccess ? 'Cuisine Added!' : 'Add Cuisine'}</Typography>
          {!creating && <IconButton size="small" onClick={() => setAddModal(false)}><CloseIcon fontSize="small" /></IconButton>}
        </DialogTitle>
        <DialogContent sx={{ px: 3 }}>
          {addSuccess
            ? <Box textAlign="center" py={2}><CheckCircleIcon sx={{ fontSize: 64, color: '#16a34a', mb: 1 }} /><Typography fontWeight={600}>Cuisine created successfully!</Typography></Box>
            : <Box pt={1} display="flex" flexDirection="column" gap={2}>
                <TextField label="Cuisine Name" placeholder="e.g. Italian, Chinese..." value={newName} onChange={e => setNewName(e.target.value)} fullWidth size="small" autoFocus sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
                <Box><Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151', mb: 1 }}>Shop Type</Typography><ShopTypeToggle value={newShopType} onChange={setNewShopType} /></Box>
                {addError && <Typography sx={{ bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', p: 1.5, color: '#dc2626', fontSize: '0.8rem' }}>{addError}</Typography>}
              </Box>
          }
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          {addSuccess
            ? <Button fullWidth variant="contained" sx={{ background: 'linear-gradient(135deg,#16a34a,#15803d)', borderRadius: '10px', fontWeight: 700, textTransform: 'none' }} onClick={() => { setAddModal(false); setAddSuccess(false) }}>Done</Button>
            : <>
                <Button onClick={() => setAddModal(false)} disabled={creating} sx={{ color: '#64748b', borderRadius: '10px', textTransform: 'none' }}>Cancel</Button>
                <Button variant="contained" onClick={handleCreate} disabled={creating} startIcon={creating ? <CircularProgress size={15} color="inherit" /> : <AddIcon />} sx={{ background: 'linear-gradient(135deg,#f59e0b,#d97706)', borderRadius: '10px', fontWeight: 700, px: 3, textTransform: 'none' }}>
                  {creating ? 'Adding...' : 'Add Cuisine'}
                </Button>
              </>
          }
        </DialogActions>
      </Dialog>

      {/* EDIT MODAL */}
      <Dialog open={editModal} onClose={() => setEditModal(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2.5, px: 3, pb: 1 }}>
          <Typography fontWeight={700} fontSize="1rem">Edit Cuisine</Typography>
          <IconButton size="small" onClick={() => setEditModal(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3 }}>
          <Box pt={1} display="flex" flexDirection="column" gap={2}>
            <TextField label="Cuisine Name" value={editName} onChange={e => setEditName(e.target.value)} fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
            <Box><Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151', mb: 1 }}>Shop Type</Typography><ShopTypeToggle value={editShopType} onChange={setEditShopType} /></Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={() => setEditModal(false)} sx={{ color: '#64748b', borderRadius: '10px', textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleEdit} disabled={editing} startIcon={editing ? <CircularProgress size={15} color="inherit" /> : <EditIcon />} sx={{ background: 'linear-gradient(135deg,#3b82f6,#2563eb)', borderRadius: '10px', fontWeight: 700, px: 3, textTransform: 'none' }}>
            {editing ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default withTranslation()(Cuisines)